import * as Oazapfts from "oazapfts/lib/runtime";
import { computed } from "vue";
import useServer from "src/composables/server/useServer";
import * as formeAPI from "src/generated/formeapi";
import {
  BillingRequest,
  ChildSchoolInfoListResponse,
  CntJournalListResponse,
  CntRequest,
  CntResponse,
  DashboardResponse,
  GenericResponse,
  JournalListResponse,
  LicenseRegisterRequest,
  LicenseVerifyRequest,
  OrganizationListResponse,
  PastCntResponse,
  PastDashboardResponse,
  SchoolListResponse,
  StatusReportListResponse,
  SurveyListRequest,
  UserListResponse,
  UserRequest,
  UserVerifyResponse,
} from "src/generated/formeapi";
import { handle } from "oazapfts";
import cookie from "js-cookie";

export default function useFormeServer() {
  const { baseUrl, manageToken } = useServer("forme");

  const requestOptions = computed((): Oazapfts.RequestOpts => {
    const jwt = cookie.get("_at");

    return {
      baseUrl: baseUrl.value,
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    };
  });

  async function v2ResourcesListUser(
    xClientModel: string
  ): Promise<UserListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesListUser(xClientModel, requestOptions.value),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2LicenseVerify(
    licenseVerifyRequest: LicenseVerifyRequest
  ): Promise<GenericResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2LicenseVerify(licenseVerifyRequest, requestOptions.value),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesCreateUser(
    userRequest: UserRequest
  ): Promise<UserListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesCreateUser(userRequest, {}, requestOptions.value),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2LicenseRegister(
    licenseRegisterRequest: LicenseRegisterRequest
  ): Promise<GenericResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2LicenseRegister(
          licenseRegisterRequest,
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesRetrieveUserDashboard(
    userUuid: string
  ): Promise<DashboardResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesRetrieveUserDashboard(
          userUuid,
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesListJournal(
    appuserUuid?: string | null
  ): Promise<JournalListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesListJournal(
          { itemsPerGroup: 1000, appuserUuid },
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesPartialUpdateUser(
    userRequest: UserRequest
  ): Promise<UserListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesPartialUpdateUser(
          userRequest,
          {},
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesUserWithdrawal(): Promise<
    GenericResponse | string
  > {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesUserWithdrawal(requestOptions.value),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesDeleteUser(
    userUuid: string
  ): Promise<GenericResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesDeleteUser(userUuid, requestOptions.value),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesRetrievePastDashboard(
    userUuid: string
  ): Promise<PastDashboardResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesRetrievePastDashboard(
          userUuid,
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesApplySurvey(
    surveyListRequest: SurveyListRequest
  ): Promise<GenericResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesApplySurvey(
          surveyListRequest,
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesApplySchoolSurvey(
    surveyListRequest: SurveyListRequest
  ): Promise<GenericResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesApplySchoolSurvey(
          surveyListRequest,
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesUserVerify(
    userRequest: UserRequest
  ): Promise<UserVerifyResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesUserVerify(userRequest, requestOptions.value),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesListUserStatusReport(
    userUuid: string
  ): Promise<StatusReportListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesListUserStatusReport(
          userUuid,
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(genericResponse) {
            return genericResponse.code + " | " + genericResponse.message;
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2BillingConfirmUser(
    userUuid: string,
    billingRequest: BillingRequest
  ): Promise<string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2BillingConfirmUser(
          userUuid,
          billingRequest,
          requestOptions.value
        ),
        {
          200(res) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2BillingCancleUser(
    userUuid: string,
    billingRequest: BillingRequest
  ): Promise<GenericResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2BillingCancleUser(
          userUuid,
          billingRequest,
          requestOptions.value
        ),
        {
          200(res: GenericResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesListOrganization(): Promise<
    OrganizationListResponse | string
  > {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesListOrganization(requestOptions.value),
        {
          200(res: OrganizationListResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourceChildSchoolInfo(
    sidoCode: number,
    sggCode: number
  ): Promise<ChildSchoolInfoListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourceChildSchoolInfo(
          sidoCode,
          sggCode,
          requestOptions.value
        ),
        {
          200(res: ChildSchoolInfoListResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourceSchool(
    sidoCode: number,
    sggCode: number,
    schoolType: string
  ): Promise<SchoolListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourceSchool(
          sidoCode,
          sggCode,
          schoolType,
          requestOptions.value
        ),
        {
          200(res: SchoolListResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesApplyCnt(
    cntRequest: CntRequest,
    {
      xAppVersion,
      xAppAgent,
    }: {
      xAppVersion?: string;
      xAppAgent?: string;
    } = {}
  ): Promise<CntResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesApplyCnt(
          cntRequest,
          {
            xAppVersion,
            xAppAgent,
          },
          requestOptions.value
        ),
        {
          200(res: CntResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesRetrieveCnt({
    xAppVersion,
    xAppAgent,
  }: {
    xAppVersion?: string;
    xAppAgent?: string;
  } = {}): Promise<CntResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesRetrieveCnt(
          {
            xAppVersion,
            xAppAgent,
          },
          requestOptions.value
        ),
        {
          200(res: CntResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesListCntJournal({
    xAppVersion,
    xAppAgent,
    itemsPerGroup,
    appuserUuid,
  }: {
    xAppVersion?: string;
    xAppAgent?: string;
    itemsPerGroup?: number | null;
    appuserUuid?: string | null;
  } = {}): Promise<CntJournalListResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesListCntJournal(
          {
            xAppVersion,
            xAppAgent,
            itemsPerGroup,
            appuserUuid,
          },
          requestOptions.value
        ),
        {
          200(res: CntJournalListResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesRetrievePastCnt(): Promise<
    PastCntResponse | string
  > {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesRetrievePastCnt(requestOptions.value),
        {
          200(res: PastCntResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  async function v2ResourcesRetrievePastCntDashboard(
    userUuid: string
  ): Promise<PastDashboardResponse | string> {
    try {
      await manageToken();

      return await handle(
        formeAPI.v2ResourcesRetrievePastCntDashboard(
          userUuid,
          requestOptions.value
        ),
        {
          200(res: PastDashboardResponse) {
            return res;
          },
          418(res) {
            return JSON.stringify(res);
          },
          common(status: number, data: any) {
            return JSON.stringify(data);
          },
        }
      );
    } catch (e) {
      return e + "";
    }
  }

  return {
    v2ResourcesListUser,
    v2LicenseVerify,
    v2ResourcesCreateUser,
    v2LicenseRegister,
    v2ResourcesRetrieveUserDashboard,
    v2ResourcesListJournal,
    v2ResourcesPartialUpdateUser,
    v2ResourcesUserWithdrawal,
    v2ResourcesDeleteUser,
    v2ResourcesRetrievePastDashboard,
    v2ResourcesApplySurvey,
    v2ResourcesUserVerify,
    v2ResourcesListUserStatusReport,
    v2BillingConfirmUser,
    v2BillingCancleUser,
    v2ResourcesApplySchoolSurvey,
    v2ResourcesListOrganization,
    v2ResourceChildSchoolInfo,
    v2ResourceSchool,
    v2ResourcesApplyCnt,
    v2ResourcesRetrieveCnt,
    v2ResourcesListCntJournal,
    v2ResourcesRetrievePastCnt,
    v2ResourcesRetrievePastCntDashboard,
  };
}
